// Context menu for chat items: rename, move to folder, download, delete.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import type { ChatFolder } from '../../types';
import { getActiveZoom } from '../../lib/zoomPosition';

interface ChatItemMenuProps {
  sessionId: string;
  sessionFolderId?: string;
  anchorRect: DOMRect;
  folders: ChatFolder[];
  onRename: () => void;
  onMoveToFolder: (folderId: string | undefined) => void;
  onDownloadPlain: () => void;
  onDownloadDetailed: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export default function ChatItemMenu({
  sessionFolderId,
  anchorRect,
  folders,
  onRename,
  onMoveToFolder,
  onDownloadPlain,
  onDownloadDetailed,
  onDelete,
  onClose,
}: ChatItemMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const [subMenuFlip, setSubMenuFlip] = useState(false);

  // Position (unscaled CSS px; see getActiveZoom)
  const z = getActiveZoom();
  const top = anchorRect.bottom / z + 4;
  const left = Math.min(anchorRect.left / z, window.innerWidth / z - 200);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubMenuHover = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setSubMenuOpen(true);
    // Check if submenu would overflow right edge (in unscaled CSS px)
    const zHover = getActiveZoom();
    const rect = e.currentTarget.getBoundingClientRect();
    setSubMenuFlip(rect.right / zHover + 180 > window.innerWidth / zHover);
  }, []);

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      className="malpedia-context-menu"
      style={{ top, left }}
      role="menu"
    >
      <button
        className="malpedia-context-menu-item"
        role="menuitem"
        onClick={() => { onRename(); onClose(); }}
      >
        Rename
      </button>

      <div
        className="malpedia-context-menu-item malpedia-context-menu-submenu-trigger"
        onMouseEnter={handleSubMenuHover}
        onMouseLeave={() => setSubMenuOpen(false)}
      >
        <span>Move to folder</span>
        <span className="malpedia-context-menu-arrow">▶</span>
        {subMenuOpen && (
          <div
            className={`malpedia-context-menu malpedia-context-submenu${subMenuFlip ? ' flip' : ''}`}
            role="menu"
          >
            {folders.map((f) => (
              <button
                key={f.id}
                className={`malpedia-context-menu-item${sessionFolderId === f.id ? ' current' : ''}`}
                role="menuitem"
                onClick={() => { onMoveToFolder(f.id); onClose(); }}
              >
                📂 {f.name}
                {sessionFolderId === f.id && <span className="malpedia-context-menu-check">✓</span>}
              </button>
            ))}
            {folders.length > 0 && <div className="malpedia-context-menu-divider" />}
            <button
              className={`malpedia-context-menu-item${!sessionFolderId ? ' current' : ''}`}
              role="menuitem"
              onClick={() => { onMoveToFolder(undefined); onClose(); }}
            >
              Unfiled
              {!sessionFolderId && <span className="malpedia-context-menu-check">✓</span>}
            </button>
          </div>
        )}
      </div>

      <div className="malpedia-context-menu-divider" />

      <button
        className="malpedia-context-menu-item"
        role="menuitem"
        onClick={() => { onDownloadPlain(); onClose(); }}
      >
        Download (Plain)
      </button>
      <button
        className="malpedia-context-menu-item"
        role="menuitem"
        onClick={() => { onDownloadDetailed(); onClose(); }}
      >
        Download (Detailed)
      </button>

      <div className="malpedia-context-menu-divider" />

      <button
        className="malpedia-context-menu-item malpedia-context-menu-item--danger"
        role="menuitem"
        onClick={() => { onDelete(); onClose(); }}
      >
        Delete
      </button>
    </div>,
    document.body,
  );
}
